import { useTranslation } from 'react-i18next';

import type { BigHeaderType, HeaderType } from 'components/stateless/Table/TableHeader';
import type { StatisticsOfAnchorAgreementVOModel } from 'models/vo/StatisticsOfAnchorAgreementVO';
import type { ColumnOption, MergedCellTypes } from 'utils/spreadSheet/types';

export function useSystemOverviewByAnchorListChannelTabConstants() {
  const { t } = useTranslation(['format']);

  const INVOICE_TABLE_HEADER: HeaderType[] = [
    {
      headerText: t('text:Financier_Name'),
    },
    {
      headerText: t('text:Anchor_Name'),
    },
    {
      headerText: t('text:Anchor_Client_Code'),
    },
    {
      headerText: t('text:Anchor_Master_Agreement_Number'),
    },
    {
      headerText: t('text:Currency'),
    },
    {
      headerText: t('text:Financing_Balance_/_Count'),
      className: 'main100',
    },
    {
      headerText: t('text:Invoice_Amount_to_be_paid_/_Count'),
      className: 'main100',
    },
    {
      headerText: t('text:Registered_Invoice_Amount_/_Count'),
      className: 'main200',
    },
    {
      headerText: t('text:Total_Financing_Amount_/_Count'),
      className: 'main200',
    },
    {
      headerText: t('text:Total_Non_proceeded_Invoice_Amount_/_Count'),
      className: 'main200',
    },
  ];
  const INVOICE_BIG_TABLE_HEADER: BigHeaderType[] = [
    {
      headerText: t('text:Financier_Information'),
      colSpan: 1,
    },
    {
      headerText: t('text:Anchor_Information'),
      colSpan: 4,
    },
    {
      headerText: t('text:Associated_Partner_Current_Usage_Information'),
      colSpan: 2,
      className: 'main100',
    },
    {
      headerText: t('text:Associated_Partner_Accumulated_Usage_Information'),
      colSpan: 3,
      className: 'main200',
    },
  ];
  const excelColumnsInvoice: ColumnOption<StatisticsOfAnchorAgreementVOModel>[] = [
    {
      header: t('text:Financier_Name'),
      key: 'financierName',
    },
    {
      header: t('text:Anchor_Name'),
      key: 'anchorFinancierClientName',
    },
    {
      header: t('text:Anchor_Client_Code'),
      key: 'anchorFinancierClientCode',
    },
    {
      header: t('text:Anchor_Master_Agreement_Number'),
      key: 'contractNo',
    },
    {
      header: t('text:Currency'),
      key: 'currencyType',
    },
    {
      header: t('text:Outstanding_Financing_Balance'),
      key: 'currentLoanBalanceAmount',

      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Number_of_Outstanding_Financing'),
      key: 'currentDisbursedLoanCount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Invoice_Amount_to_be_paid'),
      key: 'currentSettlementWaitingInvoiceAmount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Number_of_Invoices_to_be_paid'),
      key: 'currentSettlementWaitingInvoiceCount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Registered_Invoice_Amount'),
      key: 'registeredInvoiceAmount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Number_of_Registered_Invoices'),
      key: 'registeredInvoiceCount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Total_Financing_Amount'),
      key: 'totalDisbursedLoanAmount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Total_Number_of_Financing'),
      key: 'totalDisbursedLoanCount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Total_Amount_of_Invoices_Not_Proceeded'),
      key: 'totalUnsettledInvoiceAmount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Total_Number_of_Invoices_Not_Proceeded'),
      key: 'totalUnsettledInvoiceCount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
  ];
  const invoiceMergedCells: MergedCellTypes[] = [
    {
      target: 'A:A',
      rowIndex: 1,
      value: t('text:Financier_Information'),
    },
    {
      target: 'B:E',
      rowIndex: 1,
      value: t('text:Anchor_Information'),
    },
    {
      target: 'F:I',
      rowIndex: 1,
      value: t('text:Associated_Partner_Current_Usage_Information'),
      style: {
        fill: {
          fgColor: { argb: 'FFFFFCDE' },
        },
      },
    },
    {
      target: 'J:O',
      rowIndex: 1,
      value: t('text:Associated_Partner_Accumulated_Usage_Information'),
      style: {
        fill: {
          fgColor: { argb: 'FFFFF8BD' },
        },
      },
    },
  ];
  const invoiceMergedTableHeader: BigHeaderType[] = [
    {
      headerText: t('text:Financier_Information'),
      colSpan: 1,
    },
    {
      headerText: t('text:Anchor_Information'),
      colSpan: 4,
      className: 'main100',
    },
    {
      headerText: t('text:Associated_Partner_Current_Usage_Information'),
      colSpan: 4,
      className: 'main200',
    },
    {
      headerText: t('text:Associated_Partner_Accumulated_Usage_Information'),
      colSpan: 6,
      className: 'main300',
    },
  ];

  const invoiceTableHeader: HeaderType[] = [
    {
      headerText: t('text:Financier_Name'),
    },
    {
      headerText: t('text:Anchor_Name'),
      className: 'main100',
    },
    {
      headerText: t('text:Anchor_Client_Code'),
      className: 'main100',
    },
    {
      headerText: t('text:Anchor_Master_Agreement_Number'),
      className: 'main100',
    },
    {
      headerText: t('text:Currency'),
      className: 'main100',
    },
    {
      headerText: t('text:Outstanding_Financing_Balance'),
      className: 'main200',
    },
    {
      headerText: t('text:Number_of_Outstanding_Financing'),
      className: 'main200',
    },
    {
      headerText: t('text:Invoice_Amount_to_be_paid'),
      className: 'main200',
    },
    {
      headerText: t('text:Number_of_Invoices_to_be_paid'),
      className: 'main200',
    },
    {
      headerText: t('text:Registered_Invoice_Amount'),
      className: 'main300',
    },
    {
      headerText: t('text:Number_of_Registered_Invoices'),
      className: 'main300',
    },
    {
      headerText: t('text:Total_Financing_Amount'),
      className: 'main300',
    },
    {
      headerText: t('text:Total_Number_of_Financing'),
      className: 'main300',
    },
    {
      headerText: t('text:Total_Amount_of_Invoices_Not_Proceeded'),
      className: 'main300',
    },
    {
      headerText: t('text:Total_Number_of_Invoices_Not_Proceeded'),
      className: 'main300',
    },
  ];

  return {
    INVOICE_TABLE_HEADER,
    INVOICE_BIG_TABLE_HEADER,
    excelColumnsInvoice,
    invoiceMergedCells,
    invoiceMergedTableHeader,
    invoiceTableHeader,
  };
}
